import {
  type AcknowledgementRecord,
  isAcknowledged,
  type TargetAcknowledgement,
  watermarkOf,
} from "@storyboard/dispatch-view/acknowledgement";
import { DRIVER_ITEM_READ_EVENTS, ITEM_READ_EVENTS } from "@/models/actions/readEvents";
import { formatItemDate, isFirstItemOfDateGroup } from "@/models/feedItemDateGrouping";
import type { AccountEvent, File, Item, Link, MemberAvailability, TmsEventRow, TriggerType } from "@/store/types";

/**
 * A message in a driver's thread: the item, its text, audio, files, and links, who sent it, and, for a dispatcher's
 * message, whether the driver has seen or heard it.
 */
export type ThreadMessage = {
  kind: "message";
  id: string;
  at: string;
  /** The driver's own message, drawn on the left; anything else (a dispatcher, an automation) sits on the right. */
  fromDriver: boolean;
  /** "Automated" for a bot, else the sender's name; `null` when the store has no account for it yet. */
  senderName: string | null;
  text: string | null;
  /** Playable audio, when the item has an encoding this browser can play. */
  audio: { seconds: number } | null;
  /** The files attached to the message, in the order stored. */
  files: File[];
  /** The links the message carries, in their stored order. */
  links: Link[];
  /**
   * Whether the message's content has been fetched at all. Until it has, a message with nothing to show is loading,
   * not still being transcribed.
   */
  contentLoaded: boolean;
  /** Whether the driver has seen a dispatcher's message, which hearing it implies; false on the driver's own. */
  seen: boolean;
  /** The driver's listen receipt on a dispatcher's message; null until it has played, and on the driver's own. */
  listened: ListenReceipt | null;
  /** Whether a dispatcher has handled it, so the bubble can carry the red dot while it waits. */
  acknowledged: boolean;
  /** Everyone's receipts on this message, oldest first: who saw it, who heard it, who acknowledged it. */
  activity: MessageActivity[];
};

/**
 * Why a driver's playback counts as merely played rather than heard: the device was muted when the message played,
 * or the driver was not driving (on duty, off duty, in the sleeper), so nobody may have been in the cab to hear it.
 */
export type PlayedReason = "deviceMuted" | "driverMaybeAbsent";

/**
 * How a heard playback reached the driver: delivered hands-free while they were driving, started by the driver with
 * the play button, or asked for by voice (a repeat, or the Storyboard catch-up command, which the device reports the
 * same way).
 */
export type HeardReason = "driving" | "manualPlay" | "repeated";

/**
 * What a driver's playbacks of a message add up to. A message is heard when any playback found the driver present:
 * driving with hands-free on and the volume up, or started by the driver by hand or by a voice command (repeat, or
 * catching up), which the server records as present whatever the duty status. Otherwise it was only played, with the
 * reason the latest playback gives.
 */
export type ListenReceipt = { kind: "heard"; reason: HeardReason } | { kind: "played"; reason: PlayedReason };

/**
 * One receipt on a message, as the activity list shows it. A heard line's reason is the driver's listen receipt's;
 * it is null on a colleague's playback, whose trigger says nothing about the driver.
 */
export type MessageActivity = { actorName: string | null; at: string } & (
  | { kind: "seen" | "acknowledged" }
  | { kind: "heard"; reason: HeardReason | null }
  | { kind: "played"; reason: PlayedReason }
);

/**
 * Something that happened on the thread rather than something someone said: a channel acknowledgement naming who
 * handled the channel, or a TMS event the driver's day produced — an arrival, a call, a fuel stop. A message's own
 * acknowledgement is not an event: it belongs to the message, in its activity list.
 */
export type ThreadEvent =
  | { kind: "event"; id: string; at: string; event: "acknowledged"; actorName: string | null }
  | { kind: "event"; id: string; at: string; event: "tmsEvent"; tmsEvent: TmsEventRow };

export type ThreadEntry = ThreadMessage | ThreadEvent;

/** A channel's acknowledgement log, folded: its watermark rows, and each message's own. */
export type ThreadAcknowledgements = {
  feed: TargetAcknowledgement;
  itemFold: (feedItemId: string) => TargetAcknowledgement;
};

/** What the thread builder needs from the store, passed in so the builder stays pure and testable. */
export type ThreadSources = {
  items: readonly Item[];
  /** The driver whose channel this is; their account's events are the receipts. */
  driverAccountId: string | null;
  acknowledgements: ThreadAcknowledgements;
  /** Who a membership is, for an acknowledgement's actor; null when the store has no name for it yet. */
  actorNameFor: (membershipId: string | null | undefined) => string | null;
  /** Whether the view asks its team to acknowledge the driver's messages; when not, no message waits. */
  messagesRequireAcknowledgement?: boolean;
  /** Every account event on the feed's items, by item id. */
  eventsByItem: ReadonlyMap<string, readonly AccountEvent[]>;
  textFor: (contentId: string) => string | null;
  audioSecondsFor: (contentId: string) => number | null;
  filesFor: (contentId: string) => File[];
  linksFor: (contentId: string) => Link[];
  nameFor: (accountId: string | null | undefined) => { name: string | null; bot: boolean } | null;
  now: number;
  /** The driver's TMS events; absent or empty when none are loaded, and the thread is then messages only. */
  tmsEvents?: readonly TmsEventRow[];
  /** Whether the feed has no older history left to fetch (its `loadedFirstPage`), which lifts the events' floor. */
  historyExhausted: boolean;
};

const HEARD_EVENTS: ReadonlySet<string> = new Set([...DRIVER_ITEM_READ_EVENTS, "Finished Listening to Feed Item"]);
const SEEN_EVENTS: ReadonlySet<string> = new Set(ITEM_READ_EVENTS);

/** The one availability under which a playback counts as heard; the server's `MemberAvailability.present`. */
const PRESENT: MemberAvailability = "present";

/**
 * Why a heard playback reached the driver, from the trigger the device reported. A trigger the row does not carry,
 * or one the device could not name, reads as hands-free delivery while driving: that is the only way a playback is
 * heard without the driver starting it. Rows persisted before the store kept the trigger restore with it `undefined`
 * rather than null, so that is a missing trigger too.
 */
function heardReasonFor(triggerType: TriggerType | null | undefined): HeardReason {
  switch (triggerType) {
    case "button":
      return "manualPlay";
    case "voice":
      return "repeated";
    case "handsfree":
    case "unknown":
    case null:
    case undefined:
      return "driving";
  }
}

/** The driver's listen receipt on a message, and the instant it dates from. */
type DatedListenReceipt = { receipt: ListenReceipt; at: string };

/**
 * How one of the driver's playbacks reads on its own. The server stamps each playback with the driver's availability
 * at the time, present only when the driver was driving with hands-free on and the volume up or asked for the
 * playback themselves; a muted device is read from the playback's own volume, as the classic view reads it. An
 * availability the row does not carry reads as absent on purpose: the playback happened, and nothing says the driver
 * was there for it. The server defaults the column to `unknown`, so the case is all but theoretical.
 */
function listenReceiptForEvent(
  event: Pick<AccountEvent, "memberAvailability" | "deviceVolume" | "triggerType">,
): ListenReceipt {
  // Only a zero is muted. A missing reading means the device reported no volume, which is not evidence that it was
  // silenced; `MembershipAvailabilityCalculator` lets a null fall through for the same reason.
  if (event.deviceVolume === 0) return { kind: "played", reason: "deviceMuted" };
  if (event.memberAvailability === PRESENT) return { kind: "heard", reason: heardReasonFor(event.triggerType) };
  return { kind: "played", reason: "driverMaybeAbsent" };
}

/**
 * The driver's listen receipt on a message from the playbacks the store holds for it, or null when the message never
 * played for them. Heard outranks played among those rows, dated to the first that reached the driver; short of that,
 * the latest row says why it was only played. The store keeps one row per event name per account per message and lets
 * a later report replace it (`mergeAccountEvent`), so the rows here are at most a Started and a Finished playback,
 * each carrying the device's latest word under that name. A replay the driver asks for after a muted or absent
 * playback lifts the receipt that way: its report replaces the old one.
 */
function driverListenReceipt(playbacks: readonly AccountEvent[]): DatedListenReceipt | null {
  const byTime = [...playbacks]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((event) => ({ receipt: listenReceiptForEvent(event), at: event.createdAt }));
  return byTime.find((row) => row.receipt.kind === "heard") ?? byTime.at(-1) ?? null;
}

/** The kind of activity line one account event makes, or null when the event is not a receipt. */
function activityKindOf(event: AccountEvent): "heard" | "seen" | null {
  if (HEARD_EVENTS.has(event.name)) return "heard";
  if (SEEN_EVENTS.has(event.name)) return "seen";
  return null;
}

/**
 * Everyone's receipts on one message, oldest first: a seen per account (the earliest), a heard per account other than
 * the driver (the earliest), the driver's own listen receipt as `buildDispatchThread` computed it, and the message's
 * standing acknowledgements. The sender's own receipts are left out; a person seeing their own message says nothing.
 */
function messageActivity(
  item: Pick<Item, "id" | "accountId">,
  events: readonly AccountEvent[],
  driver: { accountId: string | null; listened: DatedListenReceipt | null },
  own: TargetAcknowledgement,
  nameFor: ThreadSources["nameFor"],
  actorNameFor: ThreadSources["actorNameFor"],
): MessageActivity[] {
  const earliest = new Map<string, MessageActivity>();
  for (const event of events) {
    if (event.accountId === item.accountId) continue;
    const kind = activityKindOf(event);
    if (!kind) continue;
    // The driver's playbacks were weighed together already: only their availability says whether one was heard.
    if (kind === "heard" && event.accountId === driver.accountId) continue;
    const key = `${kind}:${event.accountId}`;
    const known = earliest.get(key);
    if (!known || event.createdAt < known.at) {
      const actorName = nameFor(event.accountId)?.name ?? null;
      const line: MessageActivity =
        kind === "heard"
          ? { kind, reason: null, actorName, at: event.createdAt }
          : { kind, actorName, at: event.createdAt };
      earliest.set(key, line);
    }
  }
  const listened: MessageActivity[] = driver.listened
    ? [{ ...driver.listened.receipt, actorName: nameFor(driver.accountId)?.name ?? null, at: driver.listened.at }]
    : [];
  const acknowledged: MessageActivity[] = own.standing.map((row) => ({
    kind: "acknowledged",
    actorName: actorNameFor(actorOf(row)),
    at: row.createdAt,
  }));
  return [...earliest.values(), ...listened, ...acknowledged].sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * The driver's TMS events from `floor` to now, oldest first; a `null` floor admits every event up to now.
 *
 * The floor is the oldest loaded message while older history may exist, so a chip never sits in a hole of unfetched
 * history reading as the driver's whole day; once the feed has nothing older to fetch there is no hole, and no floor.
 */
function tmsEventsInSpan(events: readonly TmsEventRow[], floor: string | null, now: number): ThreadEvent[] {
  const newest = new Date(now).toISOString();
  return events
    .filter((event) => (floor === null || event.occurredAt >= floor) && event.occurredAt <= newest)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.id.localeCompare(b.id))
    .map((event) => ({
      kind: "event",
      id: `tmsEvent:${event.id}`,
      at: event.occurredAt,
      event: "tmsEvent",
      tmsEvent: event,
    }));
}

/**
 * Builds the thread a driver's History pane shows: the live messages oldest first, with every standing channel
 * acknowledgement and every TMS event of the driver's as events in time order among them — a tie puts the message
 * first. A dispatcher's message carries the driver's seen receipt and listen receipt (heard, or only played and why).
 * A message's own acknowledgement is not an event; it rides on the message as activity, beside who saw and heard it.
 * An acknowledgement that was undone is not there at all.
 */
export function buildDispatchThread(sources: ThreadSources): ThreadEntry[] {
  const { items, driverAccountId, acknowledgements, eventsByItem, textFor, audioSecondsFor, nameFor, now } = sources;
  const { filesFor, linksFor, tmsEvents = [], actorNameFor, messagesRequireAcknowledgement = true } = sources;
  const watermark = watermarkOf(acknowledgements.feed);
  const messages: ThreadMessage[] = items
    .filter((item) => item.deletedAt == null)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((item) => {
      const fromDriver = item.isFromAliasMember === true;
      const sender = nameFor(item.accountId);
      const events = eventsByItem.get(item.id) ?? [];
      const driverEvents = events.filter((event) => driverAccountId != null && event.accountId === driverAccountId);
      // The driver's receipt on a dispatcher's message, computed once for the caption and the activity list alike.
      const driverListened = fromDriver
        ? null
        : driverListenReceipt(driverEvents.filter((event) => HEARD_EVENTS.has(event.name)));
      const seconds = audioSecondsFor(item.contentId);
      return {
        kind: "message",
        id: item.id,
        at: item.createdAt,
        fromDriver,
        senderName: fromDriver ? (sender?.name ?? null) : sender ? (sender.bot ? "Automated" : sender.name) : null,
        text: textFor(item.contentId),
        audio: seconds != null && seconds > 0 ? { seconds } : null,
        files: filesFor(item.contentId),
        links: linksFor(item.contentId).sort((a, b) => a.linkOrder - b.linkOrder),
        contentLoaded: item.loadedContent === true,
        seen: !fromDriver && driverEvents.some((event) => SEEN_EVENTS.has(event.name) || HEARD_EVENTS.has(event.name)),
        listened: driverListened?.receipt ?? null,
        acknowledged:
          !fromDriver ||
          !messagesRequireAcknowledgement ||
          isAcknowledged({ arrivedAt: item.createdAt, own: acknowledgements.itemFold(item.id), watermark, now }),
        activity: messageActivity(
          item,
          events,
          { accountId: driverAccountId, listened: driverListened },
          acknowledgements.itemFold(item.id),
          nameFor,
          actorNameFor,
        ),
      };
    });

  const acknowledgedEvents: ThreadEvent[] = acknowledgements.feed.standing.map((row) => ({
    kind: "event",
    id: row.id,
    at: row.createdAt,
    event: "acknowledged",
    actorName: actorNameFor(actorOf(row)),
  }));
  // Not exhausted and no messages yet means the opening page is still in flight: no floor to hold events to.
  const floor = sources.historyExhausted ? null : messages[0]?.at;
  const spanEvents = floor === undefined ? [] : tmsEventsInSpan(tmsEvents, floor, now);
  const channelEvents = [...acknowledgedEvents, ...spanEvents].sort((a, b) => a.at.localeCompare(b.at));

  const timeline: ThreadEntry[] = [];
  let nextEvent = 0;
  for (const message of messages) {
    while (nextEvent < channelEvents.length && (channelEvents[nextEvent]?.at ?? "") < message.at) {
      timeline.push(channelEvents[nextEvent] as ThreadEvent);
      nextEvent += 1;
    }
    timeline.push(message);
  }
  timeline.push(...channelEvents.slice(nextEvent));
  return timeline;
}

/** What the History pane shows of its thread; every kind shown is the whole thread. */
export type ThreadFilters = {
  /** Shows the TMS event pills; messages and acknowledgement events always show. */
  showTmsEvents: boolean;
};

/**
 * The thread as the History pane shows it under `filters`: TMS events dropped when they are not shown. With
 * everything shown it is the input array itself, so a caller memoizing on it sees no change.
 */
export function filterThreadEntries(entries: ThreadEntry[], { showTmsEvents }: ThreadFilters): ThreadEntry[] {
  if (showTmsEvents) return entries;
  return entries.filter((entry) => !(entry.kind === "event" && entry.event === "tmsEvent"));
}

/**
 * The date divider each thread entry opens with, as `MMM d, yyyy`, or null when it falls on the day of the entry before
 * it. Messages and events are compared together, as the classic timeline does, so the day's first entry of either kind
 * carries it.
 */
export function threadDateDividers(entries: readonly ThreadEntry[]): (string | null)[] {
  return entries.map((entry, index) => {
    const { shortDateStr, formattedDate } = formatItemDate(entry.at);
    return isFirstItemOfDateGroup(shortDateStr, entries[index - 1]?.at) ? formattedDate : null;
  });
}

/** The membership an acknowledgement row names as its actor. */
function actorOf(row: AcknowledgementRecord): string | null {
  return row.actorWorkspaceMembershipId ?? null;
}
